import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { Link, type LinkProps, useLocation, useNavigate } from 'react-router';
import {
  canAnimateNavigation,
  historyIndex,
  NAV_DIRECTION_DATASET_KEY,
  type NavDirection,
  popDirection,
  popSource,
  shouldInterceptClick,
} from './navTransition';

/**
 * PUSH AND POP BETWEEN SCREENS, wired to the View Transition API.
 *
 * The shape of the problem, and why it is not three lines. `document.startViewTransition`
 * wants a callback that puts the DOM in its NEW state, and it snapshots what it finds
 * when that callback's promise settles. React under `<BrowserRouter>` does not work that
 * way: the location update is wrapped in `React.startTransition`, so calling `navigate()`
 * schedules a render and returns long before anything is on screen.
 *
 * So the callback navigates and then returns a promise this component resolves from a
 * LAYOUT EFFECT on the new location - after React has committed the new screen and before
 * the browser paints it, which is exactly the moment the new snapshot has to be taken.
 *
 * THE CAP EXISTS BECAUSE THE RECORD PAGE IS LAZY. If the route's chunk is not in memory,
 * React holds the old screen while it loads and the location does not commit; the API's
 * own timeout is four seconds, and four seconds of frozen screen is worse than no
 * animation. After the cap the transition is SKIPPED - the navigation still happens, it
 * just happens the way it did before this file existed. Degrading to instant is the
 * failure mode this whole mechanism is allowed to have.
 */
type RunTransition = (direction: NavDirection, act: () => void) => void;

/**
 * The default RUNS THE NAVIGATION and animates nothing. A link rendered outside the
 * provider - the kitchen sink lives outside the shell, and tests render fragments -
 * must still navigate, so the fallback is the plain call rather than a thrown error.
 */
const RouteTransitionContext = createContext<RunTransition>((_direction, act) => {
  act();
});

export function useRouteTransition(): RunTransition {
  return useContext(RouteTransitionContext);
}

/**
 * How long the new screen has to appear before the animation is abandoned. Deliberately
 * larger than --duration-nav (240ms) and far smaller than the API's four-second default:
 * it is a budget for a lazy chunk, not for a network round trip.
 */
const COMMIT_CAP_MS = 400;

export function RouteTransitionProvider({ children }: { readonly children: ReactNode }) {
  const location = useLocation();
  /** Set while a transition waits for React to commit the screen it asked for. */
  const pending = useRef<(() => void) | null>(null);
  /**
   * The last two committed positions in the history stack. Two and not one, because a
   * popstate can reach this component before or after React commits the screen it
   * caused - see `popSource`, which is where the pair is read.
   */
  const displayed = useRef<number | null>(null);
  const previouslyDisplayed = useRef<number | null>(null);
  /** The location this effect has already accounted for. */
  const committed = useRef<string | null>(null);

  useLayoutEffect(() => {
    // ONCE PER COMMITTED LOCATION. The key is read rather than only depended on,
    // which makes the guard real: StrictMode double-invokes effects in development,
    // and this one shifts a history index down a slot every time it runs.
    if (committed.current === location.key) return;
    committed.current = location.key;

    previouslyDisplayed.current = displayed.current;
    displayed.current = historyIndex(window.history.state);
    const resolve = pending.current;
    pending.current = null;
    resolve?.();
  }, [location.key]);

  const run = useCallback<RunTransition>((direction, act) => {
    // A second navigation while one is still waiting would resolve the wrong promise.
    // The user gets the destination either way; only the animation is dropped.
    if (pending.current !== null || !canAnimateNavigation()) {
      act();
      return;
    }

    const root = document.documentElement;
    root.dataset[NAV_DIRECTION_DATASET_KEY] = direction;

    const running: { current: ViewTransition | null } = { current: null };
    let capTimer: ReturnType<typeof setTimeout> | undefined;

    const transition = document.startViewTransition(() => {
      act();
      return new Promise<void>((resolve) => {
        pending.current = resolve;
        capTimer = setTimeout(() => {
          if (pending.current === null) return;
          pending.current = null;
          running.current?.skipTransition();
          resolve();
        }, COMMIT_CAP_MS);
      });
    });
    running.current = transition;

    /**
     * BOTH promises are caught, and the second one is not decoration. A skipped
     * transition - the cap firing, a second navigation interrupting this one, the
     * browser deciding not to - rejects `ready` AND `finished` with an AbortError, and
     * an unhandled rejection is a console error in every session that navigates
     * quickly. Observed in Chromium against the built bundle before this line existed.
     *
     * There is nothing to report either way: the navigation itself is unaffected, which
     * is the entire design of this mechanism.
     */
    transition.ready.catch(() => undefined);

    void transition.finished
      .catch(() => undefined)
      .finally(() => {
        if (capTimer !== undefined) clearTimeout(capTimer);
        delete root.dataset[NAV_DIRECTION_DATASET_KEY];
      });
  }, []);

  useEffect(() => {
    displayed.current = historyIndex(window.history.state);

    const onPopState = () => {
      const to = historyIndex(window.history.state);
      const direction = popDirection(
        popSource(displayed.current, previouslyDisplayed.current, to),
        to,
      );
      /**
       * The callback has nothing to do: the router registered its own `popstate`
       * listener first and that listener is what navigates. This one is here to start
       * the transition around it - and it may well arrive AFTER the router's update has
       * already committed, which is measured rather than assumed (see `popSource`).
       * That costs nothing: `startViewTransition` snapshots at the next rendering
       * opportunity, so the outgoing screen is still what the compositor holds.
       */
      run(direction, () => undefined);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [run]);

  return <RouteTransitionContext value={run}>{children}</RouteTransitionContext>;
}

export interface TransitionLinkProps extends Omit<LinkProps, 'viewTransition'> {
  /**
   * Which way this link goes in the hierarchy. It is a PROP and not a computed depth
   * because depth cannot answer it: a record and the related record beside it sit at
   * the same number of segments, and opening one from the other is a push.
   */
  readonly direction: NavDirection;
}

/**
 * A `<Link>` that animates. Everything a link does - the href in the status bar, the
 * middle click, cmd-click, the right-click menu - still comes from `<Link>`; only a plain
 * left click is taken over, and only so the transition can be started BEFORE the router
 * hears about it.
 */
export function TransitionLink({ direction, onClick, ...rest }: TransitionLinkProps) {
  const run = useRouteTransition();
  const navigate = useNavigate();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (!shouldInterceptClick(event, rest.target)) return;
    event.preventDefault();
    run(direction, () => {
      // The link's own props, forwarded by hand because this is the one navigation
      // `<Link>` is not performing. `state` is spread conditionally rather than passed
      // as `undefined`: `exactOptionalPropertyTypes` is on, and an explicit undefined
      // is not the same thing as an absent option.
      void navigate(rest.to, {
        replace: rest.replace === true,
        ...(rest.state !== undefined ? { state: rest.state } : {}),
      });
    });
  };

  return <Link {...rest} onClick={handleClick} />;
}
